import React, { useId, useState } from 'react';
import { PresenceManual } from '../types';
import { dbService } from '../services/dbService';
import { useToast } from '../contexts/ToastContext';
import { PRESENCE_CHOICES, manualToChoice } from '../utils/presence';
import { PresenceDot } from './PresenceDot';

/**
 * 내 접속 상태 3지선다 (접속중 자동 / 자리 비움 / 다른 용무 중).
 *
 * 구조는 NotificationPrivacySetting과 같다 — 네이티브 <input type="radio">를 쓰면
 * 화살표 키 이동·그룹 라벨링·스크린리더 읽기가 브라우저 기본 동작으로 따라온다.
 *
 * 계정 단위 설정이라 Tauri에서도 보인다. 다만 **접속 중에만 유지**되는 값이어서 서버가
 * DB에 쓰지 않고, 모든 세션이 끊기면 자동으로 '접속중(자동)'으로 돌아간다.
 */
interface PresenceStatusSettingProps {
  /** 지금 고른 수동값. null = 자동 판정 */
  value: PresenceManual;
  /** 서버 저장 성공 후 로컬 반영. App의 applyMyPresenceManualLocally가 유일한 구현이다. */
  onChange: (manual: PresenceManual) => void;
}

export const PresenceStatusSetting: React.FC<PresenceStatusSettingProps> = ({ value, onChange }) => {
  const groupName = useId();
  const { showToast } = useToast();
  // 저장 중에는 낙관적으로 먼저 칠한다 — 라디오는 즉시 반응하지 않으면 고장으로 보인다.
  // 실패하면 pending을 버려 서버가 아는 값(value)으로 되돌아간다.
  const [pending, setPending] = useState<'auto' | 'away' | 'busy' | null>(null);
  const [saving, setSaving] = useState(false);
  const shown = pending ?? manualToChoice(value);

  const handleSelect = async (next: 'auto' | 'away' | 'busy') => {
    if (next === shown || saving) return;
    setPending(next);
    setSaving(true);
    try {
      await dbService.updatePresenceStatus(next);
      onChange(next === 'auto' ? null : next);
    } catch {
      showToast({ type: 'error', message: '상태를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setPending(null);
      setSaving(false);
    }
  };

  return (
    <fieldset className="border-0 p-0 m-0 min-w-0">
      <legend className="text-sm font-semibold text-slate-800 mb-2">내 상태</legend>
      <div className="space-y-1">
        {PRESENCE_CHOICES.map((option) => {
          const checked = shown === option.value;
          return (
            <label
              key={option.value}
              className={
                checked
                  ? 'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer bg-brand-100 transition-colors'
                  : 'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors'
              }
            >
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={checked}
                disabled={saving}
                onChange={() => void handleSelect(option.value)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-brand-600"
              />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5 text-sm text-slate-800">
                  <PresenceDot status={option.dot} className="w-2.5 h-2.5 shrink-0" />
                  {option.label}
                </span>
                <span className="block text-xs text-slate-500 leading-relaxed">{option.description}</span>
              </span>
            </label>
          );
        })}
      </div>
      {/* 접속 중에만 유지된다는 점을 적어 둔다 — 적지 않으면 "껐다 켜면 풀린다"가 버그로 읽힌다 */}
      <p className="mt-2 px-3 text-xs text-slate-400 leading-relaxed">
        앱을 모두 종료하면 접속중(자동)으로 돌아갑니다
      </p>
    </fieldset>
  );
};
